// Libraries
import * as React from "react"
import { useState, useEffect } from "react"
import { navigate } from "gatsby"

// Services
import ApiService from "../services/api"
import EventService from "../services/event"

// Utils
import { paperingMinTickets } from "../utils/env"
import { nonAttendanceMessage } from "../utils/messages"

// Components
import TicketAdder from "./ticketAdder"
import Button from "./button"

const BuyTickets = ({ eventId }) => {
	const [qty, setQty] = useState(paperingMinTickets)
	const [event, setEvent] = useState(null)
	const [isDisabled, setIsDisabled] = useState(false)
	const [disabledReason, setDisabledReason] = useState("")
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const getEvent = async () => {
			try {
				setLoading(true)
				const eventService = new EventService()
				const response = await eventService.get(eventId)
				const eventData = response.data
				setEvent(eventData)
				if (eventData.user_non_attendance_flag) {
					setIsDisabled(true)
					setDisabledReason(nonAttendanceMessage)
				} else if (
					!eventData.papering_tickets_available_count ||
					eventData.papering_tickets_available_count <= 0
				) {
					setIsDisabled(true)
					setDisabledReason("Sorry, this event was really popular, we have allocated all tickets.")
				} else if (eventData.papering_tickets_already_purchased) {
					setIsDisabled(true)
					setDisabledReason("You have already purchased tickets for this event.")
				} else if (eventData.max_tickets_per_transaction < paperingMinTickets) {
					setIsDisabled(true)
					setDisabledReason("Sorry, unable to reserve tickets.  If this continues, please contact us.")
				}
			} catch (e) {
				setIsDisabled(true)
				setDisabledReason("Event could not be loaded.")
			} finally {
				setLoading(false)
			}
		}

		getEvent()
	}, [eventId])

	const onQtyChange = newQty => setQty(newQty)

	const handleClick = () => {
		if (qty > 0 && !isDisabled) reserveTickets()
	}

	const reserveTickets = async () => {
		if (!event) return
		const api = new ApiService()
		const body = {
			event_id: parseInt(event.id),
			tickets_requested: qty,
		}
		const response = await api.put("papering-tickets/reserve", { body: JSON.stringify(body) })
		if (response.data.success) {
			const paperingTicket = response.data.paperingTicket
			if (typeof window !== "undefined") {
				window.dataLayer = window.dataLayer || []
				window.dataLayer.push({
					event: "papering_tickets_reserve",
					event_id: event.id,
					event_name: (event?.artist?.name ?? event?.performer_name ?? "") + " - " + event.name,
					basket_item_value: paperingTicket.fee,
				})
			}
			await navigate(`/papering-tickets/pay/${paperingTicket.token}/${paperingTicket.id}`)
		} else {
			setIsDisabled(true)
			if (response.data.message === "UserNonAttendanceFlagged") {
				setDisabledReason(nonAttendanceMessage)
			} else if (response.data.message === "NoAvailableTickets") {
				setDisabledReason("Sorry, this event was really popular, we have allocated all tickets.")
			} else if (response.data.message === "UserAlreadyPurchased") {
				setDisabledReason("Sorry, you have already purchased tickets for this event.")
			} else if (response.data.message === "TicketsRequestedBelowMin") {
				setDisabledReason(`Sorry, tickets for this event must be bought in a minimum of ${paperingMinTickets}.`)
			} else {
				setDisabledReason("Sorry, unable to reserve tickets.  If this continues, please contact us.")
			}
		}
	}

	return (
		<div className="buy-tickets">
			{loading ? (
				<p className="apply__cta__deadline-passed">Loading ...</p>
			) : isDisabled ? (
				<p className="apply__cta__deadline-passed">{disabledReason}</p>
			) : (
				<>
					<TicketAdder
						max={event.max_tickets_per_transaction}
						initial={paperingMinTickets}
						min={paperingMinTickets}
						onChange={onQtyChange}
					/>
					<Button colorElectricLime onClick={handleClick} disabled={isDisabled}>
						{`Buy ${qty} Ticket${qty > 1 ? "s" : ""}`}
					</Button>
				</>
			)}
		</div>
	)
}

export default BuyTickets
